import React from "react";
import {
	FreeCourseLicenseContainer,
	FreeCourseLicenseContentImage,
	FreeCourseLicenseContentListItem,
	FreeCourseLicenseContentListWrapper,
	FreeCourseLicenseContentWrapper,
	FreeCourseLicenseFAQContainer,
	FreeCourseLicenseFAQItemContent,
	FreeCourseLicenseFAQItemHeader,
	FreeCourseLicenseFAQItemWrapper,
	FreeCourseLicenseHeader,
	FreeCourseLicenseSubtitle,
} from "@insightfulscience/atomic-react/r1/molecules/FreeCourseLicense";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Heading from "@insightfulscience/atomic-react/Heading";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import { publicConfig } from "@src/config";
import HubspotForm from "@src/utils/hubspotForm";
import PreloadImages from "@src/PreloadImages";
import SiteLayout from "../src/SiteLayout";
import resources from "../locales/free-course-license-page";

const FreeCourseLicense = () => {
	const [t] = useLocale("free-course-license-page");

	return (
		<>
			<SiteLayout hero>
				<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
					<PageMeta.OGP
						title={t("PAGE_TITLE")}
						description={t("META_DESCRIPTION")}
						locale={t("META_LOCALE")}
						type={t("META_TYPE")}
						url={t("META_URL")}
						image={t("META_IMAGE")}
						imageSecureUrl={t("META_IMAGE_SECURE")}
						siteName={t("META_SITE_NAME")}
						articlePublisher={t("META_ARTICLE_PUBLISHER")}
						isImageBaseUrl
					/>
					<PageMeta.Twitter
						title={t("PAGE_TITLE")}
						description={t("META_DESCRIPTION")}
						site={t("META_TWITTER_SITE")}
						card={t("META_TWITTER_CARD")}
						image={t("META_TWITTER_IMAGE")}
						creator={t("META_TWITTER_CREATOR")}
						isImageBaseUrl
					/>
					<PageMeta.Canonical href={t("META_CANONICAL")} />
					<PageMeta.Author content={t("META_AUTHOR")} />
					<PreloadImages imagesHref={["/images/snapgene/free-course-license/rectangle.png"]} />
				</PageMeta>
				<Hero>
					<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
					<HeroAction>
						<HeroButtonLink href="#hubspotFormWrapper">{t("HERO_LINK")}</HeroButtonLink>
					</HeroAction>
				</Hero>
				<FreeCourseLicenseContainer>
					<FreeCourseLicenseHeader>{t("STUDENT_TITLE")}</FreeCourseLicenseHeader>
					<FreeCourseLicenseSubtitle>{t("STUDENT_SUBTITLE")}</FreeCourseLicenseSubtitle>
				</FreeCourseLicenseContainer>
				<FreeCourseLicenseContentWrapper>
					<FreeCourseLicenseContentImage src="/images/snapgene/free-course-license/rectangle.png" />
					<FreeCourseLicenseContentListWrapper>
						<FreeCourseLicenseContentListItem>
							{t("STUDENT_LIST_ITEM_1")}
						</FreeCourseLicenseContentListItem>
						<FreeCourseLicenseContentListItem>
							{t("STUDENT_LIST_ITEM_2")}
						</FreeCourseLicenseContentListItem>
						<FreeCourseLicenseContentListItem>
							{t("STUDENT_LIST_ITEM_3")}
						</FreeCourseLicenseContentListItem>
						<FreeCourseLicenseContentListItem>
							{t("STUDENT_LIST_ITEM_4")}
						</FreeCourseLicenseContentListItem>
						<FreeCourseLicenseContentListItem>
							{t("STUDENT_LIST_ITEM_5")}
						</FreeCourseLicenseContentListItem>
						<FreeCourseLicenseContentListItem>
							{t("STUDENT_LIST_ITEM_6")}
						</FreeCourseLicenseContentListItem>
					</FreeCourseLicenseContentListWrapper>
				</FreeCourseLicenseContentWrapper>
				<div id="hubspotFormWrapper">
					<SimpleTemplate utility={{ bgColor: "white" }} cellWidth={9}>
						<Row utility={{ justify: "center" }}>
							<Col sm={12} md={7} spacing={{ mb: 10 }}>
								<Heading Tag="h3" sx={{ fontSize: "l", fontWeight: "normal", textAlign: "center" }}>
									Bring the power of SnapGene to your classroom
								</Heading>
							</Col>
						</Row>
						<HubspotForm
							id="hubspotFreeCourseForm"
							formId={publicConfig.HUBSPOT_FREE_COURSE_LICENSE_FORM_ID}
						/>
					</SimpleTemplate>
				</div>
				<FreeCourseLicenseFAQContainer>
					<FreeCourseLicenseHeader>{t("FAQ_TITLE")}</FreeCourseLicenseHeader>
					<FreeCourseLicenseFAQItemWrapper>
						<FreeCourseLicenseFAQItemHeader>{t("FAQ_QUESTION_1")}</FreeCourseLicenseFAQItemHeader>
						<FreeCourseLicenseFAQItemContent>{t("FAQ_ANSWER_1")}</FreeCourseLicenseFAQItemContent>
					</FreeCourseLicenseFAQItemWrapper>
					<FreeCourseLicenseFAQItemWrapper>
						<FreeCourseLicenseFAQItemHeader>{t("FAQ_QUESTION_2")}</FreeCourseLicenseFAQItemHeader>
						<FreeCourseLicenseFAQItemContent>{t("FAQ_ANSWER_2")}</FreeCourseLicenseFAQItemContent>
					</FreeCourseLicenseFAQItemWrapper>
					<FreeCourseLicenseFAQItemWrapper>
						<FreeCourseLicenseFAQItemHeader>{t("FAQ_QUESTION_3")}</FreeCourseLicenseFAQItemHeader>
						<FreeCourseLicenseFAQItemContent>{t("FAQ_ANSWER_3")}</FreeCourseLicenseFAQItemContent>
					</FreeCourseLicenseFAQItemWrapper>
					<FreeCourseLicenseFAQItemWrapper line={false}>
						<FreeCourseLicenseFAQItemHeader>{t("FAQ_QUESTION_4")}</FreeCourseLicenseFAQItemHeader>
						<FreeCourseLicenseFAQItemContent>{t("FAQ_ANSWER_4")}</FreeCourseLicenseFAQItemContent>
					</FreeCourseLicenseFAQItemWrapper>
				</FreeCourseLicenseFAQContainer>
			</SiteLayout>
		</>
	);
};

export default pipe(FreeCourseLicense, WithLocale({ resources }));
